/**
 * sc-492641 — Desktop header nav / search coordination
 * Does not replace theme.js drawer behavior; augments desktop UX.
 */
(function () {
  'use strict';

  var DESKTOP_MQ = '(min-width: 1025px)';

  function isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches;
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(function () {
    var header = document.querySelector('.site-header');
    if (!header) return;

    var desktopToggle = header.querySelector('.desktop-nav-toggle');
    var mobileMenuToggles = header.querySelectorAll('.menu-toggle');
    var searchToggles = document.querySelectorAll('.search-toggle');
    var searchBar = document.getElementById('SearchBar');
    var searchClose = searchBar ? searchBar.querySelector('.drawer-close') : null;
    var backdrop = document.querySelector('.header-search-backdrop');
    var lastSearchTrigger = null;

    function setNavOpen(open) {
      header.classList.toggle('nav-open', open);
      document.body.classList.toggle('nav-open', open);
      if (desktopToggle && !header.classList.contains('search-open')) {
        desktopToggle.setAttribute('aria-controls', 'shopify-section-navigation');
        desktopToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        desktopToggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      }
    }

    function syncTransparentHeaderScrollState() {
      var isTransparentHomepage = isDesktop()
        && header.classList.contains('homepage')
        && document.body.classList.contains('hp-header-transparent');

      if (!isTransparentHomepage) {
        header.classList.remove('scrolled');
        return;
      }

      // This theme makes <body> the scroll container, so window.scrollY stays
      // at zero even while the page moves. Check every browser-supported root
      // scroll position to keep the transparent header in sync.
      var scrollTop = Math.max(
        window.scrollY || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0
      );
      header.classList.toggle('scrolled', scrollTop > 10);
    }

    function positionSearchField() {
      if (!isDesktop() || !searchBar) return;
      var content = header.querySelector('.header-content');
      var brand = header.querySelector('.header-brand');
      var util = header.querySelector('.header-utilities.desktop-only');
      if (!content || !brand || !util) return;
      var cr = content.getBoundingClientRect();
      var ur = util.getBoundingClientRect();
      // Figma Web 1920 – 7/16:
      // X ends ~367.5, underline at 388.5 (+21), typed text + suggestions at 515 (+127 from underline)
      // Derive from brand + CSS tokens — not toggle.getBoundingClientRect() (toggle must stay absolute in search-open)
      var contentStyles = getComputedStyle(content);
      var hamburgerLeft = parseFloat(contentStyles.getPropertyValue('--hdr-hamburger-left')) || 348.5;
      var hamburgerWidth = 28;
      var gapAfterToggle = 21;
      var br = brand.getBoundingClientRect();
      var fieldLeft = Math.max(0, br.left - cr.left + hamburgerLeft + hamburgerWidth + gapAfterToggle);
      var textLeft = fieldLeft + 127;
      var right = Math.max(0, cr.right - ur.left + 20);
      content.style.setProperty('--hdr-search-field-left', fieldLeft + 'px');
      content.style.setProperty('--hdr-search-left', textLeft + 'px');
      content.style.setProperty('--hdr-search-right', right + 'px');
      document.documentElement.style.setProperty('--hdr-search-field-left', fieldLeft + 'px');
      document.documentElement.style.setProperty('--hdr-search-left', textLeft + 'px');
      document.documentElement.style.setProperty('--hdr-search-right', right + 'px');
      var inputWrapper = searchBar.querySelector('.input-wrapper.inline-submit');
      var acTop = inputWrapper
        ? Math.round(inputWrapper.getBoundingClientRect().bottom)
        : Math.round(cr.bottom);
      document.documentElement.style.setProperty('--hdr-ac-top', acTop + 'px');
    }

    function setSearchOpen(open) {
      header.classList.toggle('search-open', open);
      document.body.classList.toggle('search-open', open);
      searchToggles.forEach(function (btn) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      if (desktopToggle && !header.classList.contains('nav-open')) {
        desktopToggle.setAttribute('aria-controls', open ? 'SearchBar' : 'shopify-section-navigation');
        desktopToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        desktopToggle.setAttribute('aria-label', open ? 'Close search' : 'Open menu');
      }
      if (searchBar && isDesktop()) {
        if (open) {
          positionSearchField();
          searchBar.style.opacity = '0';
          window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
              searchBar.style.opacity = '';
            });
          });
        } else {
          searchBar.style.opacity = '';
        }
      }
    }

    function closeSearchDrawer() {
      if (!searchBar) return;
      var wasOpen = header.classList.contains('search-open') || searchBar.classList.contains('opened');
      if (searchBar.classList.contains('opened') && searchClose) {
        searchClose.click();
      }
      setSearchOpen(false);
      if (wasOpen && lastSearchTrigger && document.contains(lastSearchTrigger)) {
        // The legacy drawer runs its own close callback. Wait until it has
        // completed before restoring the trigger focus for keyboard users.
        window.setTimeout(function () {
          lastSearchTrigger.focus();
        }, 50);
      }
    }

    function closeAll() {
      setNavOpen(false);
      closeSearchDrawer();
    }

    function shouldOpenNavOnLoad() {
      var body = document.body;
      if (!body) return false;
      return body.classList.contains('template-collection')
        || body.classList.contains('template-product');
    }

    if (isDesktop() && shouldOpenNavOnLoad()) {
      setNavOpen(true);
    }

    syncTransparentHeaderScrollState();
    window.addEventListener('scroll', syncTransparentHeaderScrollState, { passive: true });
    document.addEventListener('scroll', syncTransparentHeaderScrollState, { capture: true, passive: true });

    if (desktopToggle) {
      desktopToggle.addEventListener('click', function (e) {
        if (!isDesktop()) return;
        e.preventDefault();
        e.stopPropagation();

        // When search is open, X closes search
        if (header.classList.contains('search-open') || (searchBar && searchBar.classList.contains('opened'))) {
          closeSearchDrawer();
          return;
        }

        var willOpen = !header.classList.contains('nav-open');
        if (willOpen) {
          closeSearchDrawer();
        }
        setNavOpen(willOpen);
      });
    }

    // theme.js owns the mobile drawer state. Mirror that state in the control's
    // accessible name after its click handler has updated aria-expanded.
    mobileMenuToggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        window.setTimeout(function () {
          var open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
        }, 0);
      });
    });

    searchToggles.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!isDesktop()) return;
        lastSearchTrigger = btn;
        setNavOpen(false);
        // theme.js opens the drawer; sync our class shortly after
        window.setTimeout(function () {
          if (searchBar && searchBar.classList.contains('opened')) {
            setSearchOpen(true);
          }
        }, 20);
      });
    });

    if (searchClose) {
      searchClose.addEventListener('click', function () {
        setSearchOpen(false);
      });
    }

    if (searchBar && isDesktop()) {
      var searchInputWrapper = searchBar.querySelector('.input-wrapper.inline-submit');
      var searchInput = searchBar.querySelector('input.searchspring-ac, #Search');

      if (searchInputWrapper && searchInput) {
        searchInputWrapper.addEventListener('click', function (e) {
          if (!header.classList.contains('search-open') && !searchBar.classList.contains('opened')) {
            return;
          }
          if (e.target === searchInput) {
            return;
          }
          // The underline wrapper focuses the input on empty-space clicks. Do
          // not steal submit: `.search-submit` (`>`) lives in this wrapper, and
          // keyboard activation also fires click (sc-495682).
          var clickTarget = e.target && e.target.closest
            ? e.target
            : (e.target && e.target.parentElement);
          if (clickTarget && clickTarget.closest && clickTarget.closest('.search-submit, a, button')) {
            return;
          }
          e.preventDefault();
          searchInput.focus();
        });
      }
    }

    if (backdrop) {
      backdrop.addEventListener('click', function () {
        closeSearchDrawer();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (!isDesktop()) return;
      if (header.classList.contains('search-open') || header.classList.contains('nav-open')) {
        closeAll();
      }
    });

    // Sync with theme.js drawer class "opened" on #SearchBar
    if (searchBar && window.MutationObserver) {
      var observer = new MutationObserver(function () {
        if (!isDesktop()) {
          setSearchOpen(false);
          return;
        }
        var isOpen = searchBar.classList.contains('opened');
        setSearchOpen(isOpen);
        if (isOpen) {
          setNavOpen(false);
        }
      });
      observer.observe(searchBar, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
    }

    initSearchAutocompleteChrome(positionSearchField);
    initThreeColumnHeroes();
    window.addEventListener('resize', debounce(function () {
      initThreeColumnHeroes();
      syncTransparentHeaderScrollState();
      if (header.classList.contains('search-open')) {
        positionSearchField();
      }
    }, 150));
  });

  /**
   * SearchSpring LTS AC: inject Figma "VIEW ALL RESULTS ›" under suggestions
   * and keep search field CSS vars in sync while typing.
   */
  function initSearchAutocompleteChrome(syncSearchField) {
    var searchBar = document.getElementById('SearchBar');
    if (!searchBar || !window.MutationObserver) return;

    function selectedSuggestionQuery() {
      var activeLink = searchBar.querySelector('.ss-list-option.ss-active a[href*="q="]');
      var href = activeLink && activeLink.getAttribute('href');
      var match = String(href || '').match(/[?&]q=([^&#]+)/i);

      if (!match) return '';

      try {
        return decodeURIComponent(match[1].replace(/\+/g, ' ')).trim();
      } catch (err) {
        return '';
      }
    }

    function searchQuery() {
      var input = searchBar.querySelector('input.searchspring-ac, #Search');
      var typedQuery = input && input.value ? input.value.trim() : '';
      return selectedSuggestionQuery() || typedQuery;
    }

    function searchHref() {
      var q = searchQuery();
      return '/search?view=spring&q=' + encodeURIComponent(q);
    }

    function ensureViewAll() {
      var terms = searchBar.querySelector('.ss-ac-terms');
      if (!terms) return;
      var list = terms.querySelector('.ss-list') || terms;
      var link = terms.querySelector('a.hdr-ac-view-all');
      if (!link) {
        link = document.createElement('a');
        link.className = 'hdr-ac-view-all';
        link.textContent = 'VIEW ALL RESULTS';
        list.appendChild(link);
        // SearchSpring owns the surrounding autocomplete list and can consume
        // clicks delegated from it. Handle this action at the link itself so it
        // always navigates using the query currently entered in the search box.
        link.addEventListener('click', function (event) {
          if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

          var q = searchQuery();
          if (!q) {
            event.preventDefault();
            var input = searchBar.querySelector('input.searchspring-ac, #Search');
            if (input) input.focus();
            return;
          }

          event.preventDefault();
          window.location.assign(searchHref());
        });
      }
      link.href = searchHref();
    }

    var acObserver = new MutationObserver(function () {
      if (!isDesktop()) return;
      ensureViewAll();
      if (document.body.classList.contains('ss-ac-open') || searchBar.querySelector('.ss-ac-container')) {
        if (typeof syncSearchField === 'function') {
          syncSearchField();
        }
      }
    });

    acObserver.observe(searchBar, { childList: true, subtree: true });

    searchBar.addEventListener('input', function (e) {
      if (!e.target || !e.target.matches || !e.target.matches('input.searchspring-ac, #Search')) return;
      ensureViewAll();
    });
  }

  function initThreeColumnHeroes() {
    var heroes = document.querySelectorAll('.hero-slider__outer--three-column .hp-module-a');
    heroes.forEach(function (el) {
      if (isDesktop()) {
        el.classList.add('hero-three-column');
        if (window.jQuery && window.jQuery(el).hasClass('slick-initialized')) {
          try {
            window.jQuery(el).slick('unslick');
          } catch (err) {
            /* ignore */
          }
        }
      } else {
        // Re-init slick on mobile if needed
        if (window.jQuery && !window.jQuery(el).hasClass('slick-initialized') && el.getAttribute('data-slick')) {
          try {
            window.jQuery(el).slick(JSON.parse(el.getAttribute('data-slick')));
          } catch (err) {
            /* ignore */
          }
        }
      }
    });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }
})();
